import gsap from 'gsap';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Maths } from '../_app/cuchillo/utils/Maths';

export default class BGImage {
  static _el = null;
  static _inner = null;   // wrapper intern: background + video; controla progress (fade-out per scroll)
  static _video = null;
  static _videoSrc = null;
  static _progress = 0;
  static _opacity = 0;

  static init() {
    this._el = GetBy.id('BGImage');

    // _inner: background-image + video van aquí.
    // La seva opacity la controla `progress` (fade-out en scroll).
    // L'opacity de _el la controla `show/hide/destroy` (fade-in/out de pàgina).
    // Dos responsabilitats separades, cap conflicte.
    this._inner = document.createElement('div');
    this._inner.style.cssText = [
      'position:absolute', 'inset:0', 'width:100%', 'height:100%',
      'background-size:cover', 'background-position:center',
      'background-repeat:no-repeat',
    ].join(';');
    this._el.appendChild(this._inner);
  }

  // ─── Scale (parallax) ────────────────────────────────────────────────────

  static _scale = 0;
  static get scale() { return this._scale; }
  static set scale(__n) {
    this._scale = __n;
    this._el.style.setProperty('--scale', `${this._scale}`);
  }

  // ─── Outer opacity (show / hide / destroy) ───────────────────────────────

  static get opacity() { return this._opacity; }
  static set opacity(__o) {
    this._opacity = __o;
    this._el.style.setProperty('--opacity', `${this._opacity}`);
  }

  // ─── Inner opacity (progress — scroll fade-out) ──────────────────────────
  // Actua sobre _inner, mai sobre _el. No interfereix amb show/hide.

  static set progress(__p) {
    this._progress = __p;
    if (this._inner) {
      this._inner.style.opacity = Maths.clamp(Maths.map(__p, .9, 1, 1, 0), 0, 1);
    }
  }

  // ─── Gestión del tag <video> ─────────────────────────────────────────────

  static _getOrCreateVideo() {
    if (this._video) return this._video;

    const v = document.createElement('video');
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.autoplay = false;
    v.preload = 'none';
    v.style.cssText = [
      'position:absolute', 'inset:0', 'width:100%', 'height:100%',
      'object-fit:cover', 'pointer-events:none',
    ].join(';');

    this._inner.appendChild(v);
    this._video = v;
    return v;
  }

  static _stopAndUnloadVideo() {
    if (!this._video) return;
    const v = this._video;
    v.pause();
    v.removeAttribute('src');
    v.load();
    this._videoSrc = null;
  }

  // ─── API pública ─────────────────────────────────────────────────────────

  static set(url, urlVideo) {
    BGImage.scale = 1;
    if (!this._el) return;

    // Cancel·la qualsevol destroy en curs perquè el seu onComplete
    // no sobreescrigui el contingut que estem posant ara.
    gsap.killTweensOf(this);

    // Outer reset a 0 per al fade-in entrant.
    this.opacity = 0;
    // Inner reset a 1: la progress-opacity comença neta per a la nova pàgina.
    if (this._inner) this._inner.style.opacity = '1';

    if (url) {
      this._inner.style.backgroundImage = `url('${url}')`;
    }

    if (urlVideo) {
      const v = this._getOrCreateVideo();
      if (this._videoSrc !== urlVideo) {
        this._videoSrc = urlVideo;
        v.src = urlVideo;
        v.preload = 'auto';
        v.load();
      }
    }
  }

  // Fade in — anima l'outer (_el). El vídeo hereta l'opacitat via _inner.
  static show(duration = 0.6, delay = .75, ease = 'power2.out') {
    if (!this._el) return;
    gsap.killTweensOf(this);
    gsap.to(this, { opacity: 1, duration, delay, ease });
    if (this._video) {
      this._video.play().catch(() => {});
    }
  }

  // Fade out — anima l'outer (_el).
  static hide(duration = 0.4, ease = 'power2.out') {
    if (!this._el) return;
    gsap.killTweensOf(this);
    gsap.to(this, { opacity: 0, duration, ease });
  }

  // Fade out i neteja imatge i vídeo.
  static destroy(duration = 0.4) {
    if (!this._el) return;
    gsap.killTweensOf(this);
    gsap.to(this, {
      opacity: 0,
      duration,
      ease: 'power2.out',
      onComplete: () => {
        if (this._inner) {
          this._inner.style.backgroundImage = '';
          this._inner.style.opacity = '1';
        }
        this._stopAndUnloadVideo();
      }
    });
  }
}
